import React, { useCallback, useEffect, useRef, useState } from "react";
import { SvgComponent } from "@commons/Componentes/SvgComponent/SvgComponent";
import { getComponentWithLoading } from "@commons/Utils";
import useMessage from "@commons/Hooks/useMessage";
import { ChatComposer } from "./ChatComposer";
import { ChatMensaje } from "./ChatMensaje";
import { ChatRecientes } from "./ChatRecientes";
import {
    abrirChat,
    buscarConversaciones,
    iniciarChat,
    nuevaConversacion,
    preguntar
} from "../../Services/citrusGptService";
import "./CitrusGPTChat.css";

const CitrusGPTSL = ({ showLoading, hideLoading }) => {
    const { Warning, Error: MostrarError } = useMessage();

    const [configurado, setConfigurado] = useState(true);
    const [puedeRevisar, setPuedeRevisar] = useState(false);
    const [conversacion, setConversacion] = useState(null);
    const [mensajes, setMensajes] = useState([]);
    const [recientes, setRecientes] = useState([]);
    const [pagina, setPagina] = useState(0);
    const [hayMas, setHayMas] = useState(false);
    const [cargandoMas, setCargandoMas] = useState(false);
    const [bienvenida, setBienvenida] = useState("");
    const [pregunta, setPregunta] = useState("");
    const [enviando, setEnviando] = useState(false);
    const [bloqueo, setBloqueo] = useState(null);

    const hiloRef = useRef(null);

    // El hilo se mantiene abajo: lo último respondido es lo que interesa leer.
    useEffect(() => {
        const hilo = hiloRef.current;
        if (hilo) hilo.scrollTop = hilo.scrollHeight;
    }, [mensajes, enviando]);

    const aplicar = useCallback((datos) => {
        if (!datos) return;

        if (datos.Conversacion !== undefined) setConversacion(datos.Conversacion);
        if (datos.Mensajes !== undefined) setMensajes(datos.Mensajes || []);
        if (datos.Bienvenida !== undefined) setBienvenida(datos.Bienvenida || "");
        if (datos.PuedeRevisar !== undefined) setPuedeRevisar(Boolean(datos.PuedeRevisar));
        if (datos.Configurado !== undefined) setConfigurado(Boolean(datos.Configurado));
    }, []);

    // Un fallo de permiso o de compañía no se reintenta: deja la pantalla explicada y quieta.
    const manejarFallo = useCallback((error, { bloquea = false } = {}) => {
        const mensaje = error?.Mensaje
            || "Ocurrió un error al comunicarse con el asistente. Intente de nuevo más tarde.";

        if (bloquea) {
            setBloqueo(mensaje);
            return;
        }
        // El servidor clasifica: Tipo "Warning" es algo que el usuario puede corregir,
        // "Error" es un fallo de negocio, e Interno es una excepcion no prevista.
        if (error?.Interno || error?.Tipo === "Error") {
            MostrarError({ message: mensaje });
            return;
        }
        Warning({ message: mensaje });
    }, [MostrarError, Warning]);

    /**
     * Trae una pagina del historial. Con acumular se agrega al final (boton Ver mas);
     * sin el, reemplaza el listado (primera carga o refresco tras una accion).
     */
    const cargarHistorial = useCallback(async (paginaPedida, acumular) => {
        const datos = await buscarConversaciones(paginaPedida);
        const lista = datos?.Conversaciones || [];

        setRecientes((actual) => (acumular ? [...actual, ...lista] : lista));
        setPagina(datos?.Pagina ?? paginaPedida);
        setHayMas(Boolean(datos?.HayMas));
    }, []);

    const verMas = async () => {
        if (cargandoMas || !hayMas) return;

        setCargandoMas(true);
        try {
            await cargarHistorial(pagina + 1, true);
        } catch (error) {
            manejarFallo(error);
        } finally {
            setCargandoMas(false);
        }
    };

    // Tras enviar, abrir o crear se refresca la primera pagina: la conversacion pudo
    // estrenar titulo o subir al tope del listado.
    const refrescarHistorial = useCallback(() => {
        cargarHistorial(0, false).catch(() => {});
    }, [cargarHistorial]);

    useEffect(() => {
        let vigente = true;

        showLoading();
        Promise.all([iniciarChat(), cargarHistorial(0, false)])
            .then(([datos]) => { if (vigente) aplicar(datos); })
            .catch((error) => { if (vigente) manejarFallo(error, { bloquea: true }); })
            .finally(() => { if (vigente) hideLoading(); });

        return () => { vigente = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const enviar = async () => {
        const texto = pregunta.trim();
        if (!texto || enviando || !conversacion?.Id) return;

        setEnviando(true);
        try {
            const datos = await preguntar(conversacion.Id, texto);
            aplicar(datos);
            setPregunta("");
            refrescarHistorial();

            if (datos?.Advertencia) {
                Warning({ message: datos.Advertencia });
            }
        } catch (error) {
            // La pregunta no se borra: el usuario puede corregirla y reintentar sin reescribirla.
            manejarFallo(error);
        } finally {
            setEnviando(false);
        }
    };

    const abrir = async (id) => {
        if (enviando || id === conversacion?.Id) return;

        showLoading();
        try {
            aplicar(await abrirChat(id));
            setPregunta("");
            refrescarHistorial();
        } catch (error) {
            manejarFallo(error);
        } finally {
            hideLoading();
        }
    };

    const comenzarNueva = async () => {
        if (enviando) return;

        // Sin preguntas el chat actual ya esta vacio: abrir otro no aporta nada y
        // avisarlo seria reganar al usuario por un clic inofensivo. El boton ademas
        // se muestra deshabilitado, asi que esto es solo la red de seguridad.
        if (!mensajes.length) return;

        showLoading();
        try {
            aplicar(await nuevaConversacion());
            setPregunta("");
            refrescarHistorial();
        } catch (error) {
            manejarFallo(error);
        } finally {
            hideLoading();
        }
    };

    const soloLectura = !configurado || Boolean(bloqueo) || !conversacion?.EsActiva;
    // El motivo del bloqueo cambia el texto del campo: no es lo mismo que el asistente
    // este apagado a que el chat que abrio el usuario ya este cerrado.
    const motivoSoloLectura = !configurado || bloqueo
        ? "El asistente no está disponible."
        : "Este chat está cerrado. Abra uno nuevo para seguir preguntando.";

    return (
        <div className="bootstrapiso">
            <div className="citrus-gpt">
                <div className="citrus-gpt-title">
                    <div style={{ display: "flex", gap: 14, alignItems: "flex-start" }}>
                        <span className="citrus-gpt-icon">
                            <SvgComponent iconName="chat" width="22px" height="22px" fill="currentColor" />
                        </span>
                        <div>
                            <h1>Asistente de Documentación</h1>
                            <p>Consulte la documentación del ERP y los casos ya validados por el equipo.</p>
                        </div>
                    </div>

                    <div className="citrus-gpt-title__actions">
                        {puedeRevisar && (
                            <a
                                className="citrus-gpt-btn citrus-gpt-btn--link"
                                href="/Seguridad/conocimiento_ia_list.aspx"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <SvgComponent iconName="notes" width="15px" height="15px" fill="currentColor" />
                                Revisar respuestas
                            </a>
                        )}
                        <button
                            type="button"
                            className="citrus-gpt-btn"
                            onClick={comenzarNueva}
                            disabled={soloLectura || enviando || mensajes.length === 0}
                            title="Cierra la conversación actual y abre una nueva"
                        >
                            <SvgComponent iconName="plus" width="14px" height="14px" fill="currentColor" />
                            Nuevo chat
                        </button>
                    </div>
                </div>

                {bloqueo && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--error" role="alert">
                        <SvgComponent iconName="alert" width="18px" height="18px" fill="currentColor" />
                        <span>{bloqueo}</span>
                    </div>
                )}

                {!bloqueo && !configurado && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--aviso" role="alert">
                        <SvgComponent iconName="alert" width="18px" height="18px" fill="currentColor" />
                        <span>El asistente no está configurado. Comuníquese con el administrador.</span>
                    </div>
                )}

                {!bloqueo && configurado && conversacion && !conversacion.EsActiva && (
                    <div className="citrus-gpt-alerta citrus-gpt-alerta--aviso" role="alert">
                        <SvgComponent iconName="info" width="18px" height="18px" fill="currentColor" />
                        <span>
                            Está viendo un chat cerrado. Abra uno nuevo para seguir preguntando.
                        </span>
                    </div>
                )}

                {!bloqueo && (
                    <div className="citrus-gpt-layout">
                        <ChatRecientes
                            conversaciones={recientes}
                            actualId={conversacion?.Id}
                            onAbrir={abrir}
                            ocupado={enviando}
                            hayMas={hayMas}
                            onVerMas={verMas}
                            cargando={cargandoMas}
                        />

                        <div className="citrus-gpt-panel">
                            <div className="citrus-gpt-hilo" ref={hiloRef}>
                                {mensajes.length === 0 && (
                                    <div
                                        className="citrus-gpt-vacio"
                                        dangerouslySetInnerHTML={{ __html: bienvenida }}
                                    />
                                )}

                                {mensajes.map((mensaje) => (
                                    <ChatMensaje
                                        key={mensaje.Id}
                                        mensaje={mensaje}
                                        puedeRevisar={puedeRevisar}
                                    />
                                ))}

                                {enviando && (
                                    <div className="citrus-gpt-msg citrus-gpt-msg--asistente">
                                        <span className="citrus-gpt-msg__remitente">Citrus GPT</span>
                                        <div className="citrus-gpt-msg__burbuja citrus-gpt-pensando" aria-live="polite">
                                            <span />
                                            <span />
                                            <span />
                                        </div>
                                    </div>
                                )}
                            </div>

                            <ChatComposer
                                valor={pregunta}
                                onChange={setPregunta}
                                onEnviar={enviar}
                                ocupado={enviando}
                                deshabilitado={soloLectura}
                                motivo={motivoSoloLectura}
                            />
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export const CitrusGPT = getComponentWithLoading(CitrusGPTSL);
